import { Component, computed, signal, Signal, inject } from '@angular/core';
import { AppFormComponent } from '../../../../../shared/classes/app-form';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { Method } from '@bo/ng-api';
import { Observable, tap } from 'rxjs';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { BusinessApiService } from '../../../../../_services/business-service/business-api.service';
import { BusinessDetailsRequest } from '../../../../../_interfaces/business.interface';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../../../store/app/app.selectors';
import { FormsModule } from '@angular/forms';
import { BoFormsModule } from '@bo/ng-forms';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { HttpParams } from '@angular/common/http';
import { SetBusinessStateFromReloadAppState } from '../../../../../store/app/app.actions';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-edit-business-details',
  imports: [
    FormsModule,
    BoFormsModule,
    LoaderComponent,
    FieldComponent,
    SubmitComponent,
    OffcanvasCancelBtnComponent,
    OffcanvasBaseComponent,
  ],
  templateUrl: './edit-business-details.component.html',
})
export class EditBusinessDetailsComponent extends AppFormComponent<BusinessDetailsRequest> {
  readonly offcanvasService = inject(OffcanvasService);
  readonly toastService = inject(ToastService);
  readonly businessApiService = inject(BusinessApiService);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly store = inject(Store);

  readonly businessDetails$ = this.store.select(AppSelectors.businessDetails);
  protected businessDetails = toSignal(this.businessDetails$, { initialValue: undefined });
  isEditingAddress = signal<boolean>(false);

  bizId!: string;

  public conditionalReturnUrl: Signal<string> = computed<string>(() =>
    this.router.url.includes('settings') ? '/settings' : '/your-plan',
  );

  returnParams = computed(() => {
    if (this.router.url.includes('settings')) {
      return new HttpParams().append('page', 'business');
    } else {
      return new HttpParams();
    }
  });

  protected fields: string[] = ['name', 'vat_num', 'size', 'billing_address'];

  constructor() {
    super();
    this.subscriptions.add(
      this.route.params.subscribe({
        next: (params: Params) => {
          this.bizId = params['id'];
        },
      }),
    );
  }

  createForm(): void {
    const options$ = this.businessApiService.getOptions(
      `${this.businessApiService.URL_BIZ}/${this.bizId}`,
      Method.PUT,
      true,
    );
    const data$ = this.businessDetails$ as Observable<BusinessDetailsRequest>;
    this.buildFromOptions(options$, data$).subscribe({
      next: () => {
        this.form.get('name')?.disable();
        this.form.controls['billing_address'].valueChanges.subscribe((value) => {
          this.isEditingAddress.set(value !== this.businessDetails()?.billing_address);
        });
      },
    });
  }

  override onSubmit(): void {
    const formData = this.form.getRawValue();
    // Necessary to set a blank value for DB if you want to remove your address
    if (!formData.billing_address) this.form.get('billing_address')?.setValue(' ');
    super.onSubmit();
  }

  submitData(formData: BusinessDetailsRequest): Observable<BusinessDetailsRequest> {
    return this.businessApiService
      .updateBusinessDetails(this.bizId, formData)
      .pipe(tap((data) => this.store.dispatch(new SetBusinessStateFromReloadAppState(data))));
  }

  handleSubmitSuccess(): void {
    this.toastService.show(`Your business details have been updated.`, 'iconoir-check-circle');
    this.offcanvasService.close(this.conditionalReturnUrl(), this.returnParams());
  }
}
